import { createRouter,  createWebHistory, RouteRecordRaw } from "vue-router";
import { defineAsyncComponent } from "vue";
import LoadingSpinner from "../components/LoadingSpinner.vue";
import WelcomeView from "../views/WelcomeView.vue";
import NotFoundView from "../views/NotFoundView.vue";
import ForbiddenView from "../views/ForbiddenView.vue";
import MainLayout from "../components/MainLayout.vue";
import ExcelEntryManager from "../components/ExcelEntryManager.vue";
import I18nTestView from "../views/I18nTestView.vue";
import ExcelEntryEdit from '../components/ExcelEntryEdit.vue'
import ExcelDescriptionBrowser from "../components/ExcelDescriptionBrowser.vue";
import IrrigationDashboard from '../components/IrrigationDashboard.vue';
import ColheitaComponent from '../components/ColheitaComponent.vue';
import NonDecodedPayloads from '../components/NonDecodedPayloads.vue';
import FormilyTest from '../components/FormilyTest.vue';
import SectionHubLayout from '../components/SectionHubLayout.vue';
import SectionCardDashboard from '../components/SectionCardDashboard.vue';


const asyncComponent = (componentName: string) =>
  defineAsyncComponent({
    loader: () => {
      console.log(`Loading component: ${componentName}`);
      return import(`../components/${componentName}.vue`)
        .then((component) => {
          console.log(`Component loaded successfully: ${componentName}`);
          return component;
        })
        .catch((error) => {
          console.error(`Error loading component ${componentName}:`, error);
          return import("../views/HomeView.vue");
        });
    },
    loadingComponent: LoadingSpinner,
    delay: 200,
    timeout: 10000,
    onError(error, _retry, fail) {
      console.error("Component loading failed:", error);
      fail();
    },
  });

const asyncView = (viewName: string) =>
  defineAsyncComponent({
    loader: () => {
      console.log(`Loading view: ${viewName}`);
      return import(`../views/${viewName}.vue`)
        .then((component) => {
          console.log(`View loaded successfully: ${viewName}`);
          return component;
        })
        .catch((error) => {
          console.error(`Error loading view ${viewName}:`, error);
          throw error;
        });
    },
    loadingComponent: LoadingSpinner,
    delay: 200,
    timeout: 10000,
    onError(error, _retry, fail) {
      console.error("View loading failed:", error);
      fail();
    },
  });

// Define routes with async components
const routes: Array<RouteRecordRaw> = [
  {
    path: "/403",
    component: ForbiddenView,
    meta: { public: true },
  },
  {
    path: "/:pathMatch(.*)*",
    component: NotFoundView,
   
    meta: { requiresAuth: true },
  },
  {
    path: "/login",
    component: asyncView("LoginView"),
    meta: { public: true },
  },
  {
    path: "/welcome",
    component: WelcomeView,
    meta: { public: true },
  }, {
    path: "/sample-usage",
    component: () => import("../components/SampleUsage.vue"),
    meta: { requiresAuth: true },
  }, {
    path: "/",
    component: MainLayout,
    meta: { public: true },
    children: [
      {
        path: "",
        redirect: "home",
      },
      {
        path: "home",
        component: asyncView("HomeView"),
        meta: { requiresAuth: true },
      },
      {
        path: "table",
        component: asyncComponent("TableComponent"),
        meta: { requiresAuth: true },
      },
      {
        path: "datatable",
        component: () => import("../components/DatatablesComponent.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "upload",
        component: asyncComponent("UploadComponent"),
        meta: { requiresAuth: true },
      },
      {
        path: "fibuInputChange",
        component: asyncComponent("FibuInputChangeComponent"),
        meta: { requiresAuth: true },
      },
      {
        path: "segmented",
        component: asyncView("SegmentedView"),
        meta: { requiresAuth: true },
      },
      {
        path: "timeline",
        component: asyncView("TimelineView"),
        meta: { requiresAuth: true },
      },
      {
        path: "i18n",
        component: I18nTestView,
        meta: { requiresAuth: true },
      },
      {
        path: "excelEntryManager",
        component: ExcelEntryManager,
        meta: { requiresAuth: true },
      },
      {
        path: "fuji-apples",
        component: asyncView("FujiApplesView"),
        meta: { requiresAuth: true },
      },
      {
        path: "gala-apples",
        component: asyncView("GalaApplesView"),
        meta: { requiresAuth: true },
      },
      {
        path: '/excel/edit/:id',
        component: ExcelEntryEdit,
        meta: { requiresAuth: true }
      },
      {
        path: '/excel/history',
        component: ExcelDescriptionBrowser,
        meta: { requiresAuth: true },
        props: route => ({ descricao: route.query.descricao as string })
      },
      {
        path: '/irrigation-dashboard',
        name: 'IrrigationDashboard',
        component: IrrigationDashboard,
      },
      {
        path: '/colheita',
        name: 'Colheita',
        component: ColheitaComponent,
      }, {
        path: '/payloads',
        name: 'NonDecodedPayloads',
        component: NonDecodedPayloads,
      },
      {
        path: '/map',
        name: 'map',
        component: () => import('../components/MapComponent.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/setor/:idSetor?',
        name: 'setor',
        component: () => import('../components/SetorComponent.vue'),
        props: true
      },
      {
        path: 'apple-varieties',
        component: SectionCardDashboard,
        meta: { requiresAuth: true },
      },
      {
        path: 'tests',
        component: SectionCardDashboard,
        meta: { requiresAuth: true },
      },
      {
        path: 'playground',
        component: SectionHubLayout,
        children: [
          {
            path: '',
            component: SectionCardDashboard,
            meta: { requiresAuth: true },
          },
          {
            path: 'plantacao-selector',
            name: 'PlantacaoSelector',
            component: () => import("../components/SampleUsage.vue"),
            meta: {
              requiresAuth: true,
              title: 'Plantacao Selector',
              icon: 'Crop',
              group: 'playground'
            }
          },
          {
            path: 'create-plantation',
            name: 'PlaygroundCreatePlantation',
            component: () => import('../components/PlantationCreator.vue'),
            meta: { requiresAuth: true }
          },
        ]
      },
      {
        path: 'test',
        component: SectionHubLayout,
        children: [
          {
            path: '',
            component: SectionCardDashboard,
            meta: { requiresAuth: true },
          },
          {
            path: 'formily',
            name: 'FormilyTest',
            component: FormilyTest,
            meta: {
              title: 'Formily Test',
              group: 'test'
            }
          },
        ]
      },
      {
        path: 'farmonaut',
        component: SectionHubLayout,
        children: [
          {
            path: '',
            component: SectionCardDashboard,
            meta: { requiresAuth: true },
          },
          {
            path: 'plantations',
            name: 'FarmonautPlantations',
            component: () => import('../components/PlantationLoader.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'fields',
            name: 'FarmonautFields',
            component: () => import('../components/FieldLoader.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'upload',
            name: 'FarmonautUpload',
            component: () => import('../components/FieldUploadForm.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'upload-file',
            name: 'FarmonautUploadFile',
            component: () => import('../components/FileUploadForm.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'auto-fix',
            name: 'FarmonautAutoFix',
            component: () => import('../components/AutoFixUploadForm.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'validate',
            name: 'FarmonautValidate',
            component: () => import('../components/ValidationForm.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'transform',
            name: 'FarmonautTransform',
            component: () => import('../components/TransformForm.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'comparison',
            name: 'FarmonautComparison',
            component: () => import('../components/FieldPlantationComparison.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'assignment',
            name: 'FarmonautAssignment',
            component: () => import('../components/FieldPlantationAssignment.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'create-farmonaut',
            name: 'FarmonautCreateFarmonaut',
            component: () => import('../components/PlantationFarmonautCreator.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'health',
            name: 'FarmonautHealth',
            component: () => import('../components/HealthCheck.vue'),
            meta: { requiresAuth: true }
          },
        ]
      },
    ],
  },
 
 
];


const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
});

// Add global navigation guards for logging
router.beforeEach((to, from, next) => {
  console.log(`Navigation started: ${from.path} -> ${to.path}`);
  console.log("Route meta:", to.meta);
  next();
});

router.afterEach((to, from) => {
  console.log(`Navigation completed: ${from.path} -> ${to.path}`);
});

router.onError((error) => {
  console.error("Router error:", error);
});

export default router;
